/**
 * "I have 5 limes and the recipe wants 2 tablespoons of lime juice — am I ok?"
 *
 * Answering that is arithmetic, not string matching, and it is the question the
 * app could not ask. `calculateRequiredQuantities` already subtracts counts when
 * the units agree, but it had no way to relate two DIFFERENT ingredients, so
 * limes could never pay for lime juice however many you owned.
 *
 * Deliberately conservative. `enough` is only true when the numbers say so; a
 * missing yield, an unconvertible unit, or an unknown quantity all return
 * `unknown`, which the caller shows as a question. Claiming coverage that is not
 * there sends someone to the stove without an ingredient — the one failure this
 * whole layer exists to stop.
 */

import { relationsTo, type IngredientRelation } from '../data/ingredientRelations';
import { convertBetweenUnits, areUnitsCompatible } from './unitConversion';
import type { CanonicalIngredientId } from './ingredientVocabulary';

export type YieldVerdict = 'enough' | 'short' | 'unknown';

export interface YieldAnswer {
  verdict: YieldVerdict;
  /** How much the pantry can produce, in the recipe's unit. Null when unknown. */
  available: number | null;
  /** How much the recipe wants. Null when the line carried no amount. */
  needed: number | null;
  unit?: string;
  relation?: IngredientRelation;
  /** One sentence for the confirm row, already in the user's terms. */
  explanation: string;
}

const UNKNOWN: YieldAnswer = {
  verdict: 'unknown',
  available: null,
  needed: null,
  explanation: '',
};

/**
 * Can `haveId` (of which the user owns `haveQuantity`) cover `needAmount`
 * `needUnit` of `needId`?
 */
export function yieldCoverage(
  haveId: CanonicalIngredientId,
  haveQuantity: number | undefined,
  needId: CanonicalIngredientId,
  needAmount: number | undefined,
  needUnit: string | undefined
): YieldAnswer {
  const relation = relationsTo(needId).find(candidate => candidate.from === haveId);
  if (!relation?.yield) return { ...UNKNOWN, relation };

  // A pantry row with no count is one of the thing, not none of it.
  const owned = haveQuantity && haveQuantity > 0 ? haveQuantity : 1;
  const produced = (owned / relation.yield.per) * relation.yield.amount;

  // The recipe asked for "some", not an amount. We know the pantry can make
  // some, which is worth saying, but not whether it is enough.
  if (!needAmount || !needUnit) {
    return {
      verdict: 'unknown',
      available: produced,
      needed: null,
      unit: relation.yield.unit,
      relation,
      explanation: '',
    };
  }

  if (!areUnitsCompatible(relation.yield.unit, needUnit)) return { ...UNKNOWN, relation };

  const producedInRecipeUnit = convertBetweenUnits(produced, relation.yield.unit, needUnit);
  if (producedInRecipeUnit === null || !Number.isFinite(producedInRecipeUnit)) {
    return { ...UNKNOWN, relation };
  }

  return {
    verdict: producedInRecipeUnit >= needAmount ? 'enough' : 'short',
    available: producedInRecipeUnit,
    needed: needAmount,
    unit: needUnit,
    relation,
    explanation: '',
  };
}
